/**
 * Current value for a settings search result, keyed by the row's anchor.
 *
 * The rows on each page show what a setting is set to; a search result that
 * points at one of those rows should say the same thing, so a hit often answers
 * the question without navigating (#672).
 *
 * Everything here comes off the account object the sidebar already loads
 * (`useAccount`, the shared `['account', id]` React Query cache the settings
 * pages read), so this adds no request and cannot disagree with the page it
 * points at. Settings that live somewhere else — the invoice template, the
 * notification preferences, the passkey list — deliberately return nothing
 * rather than triggering a fetch from the rail.
 */
export function settingValue(anchor: string, account: any): string | null {
  if (!account) return null;

  const money = (n: unknown) =>
    typeof n === 'number' && Number.isFinite(n) ? `$${n.toFixed(2)}` : null;
  const text = (v: unknown) => {
    const s = typeof v === 'string' ? v.trim() : '';
    return s ? s : null;
  };

  switch (anchor) {
    // ── Services ──────────────────────────────────────────────────────────
    case 'set-hourly-rate':
      return money(account.hourlyRate);
    case 'set-minimum-charge':
      return money(account.minimumCharge);
    case 'set-sales-tax':
      return account.collectsSalesTax ? 'On' : 'Off';

    // ── Payments ──────────────────────────────────────────────────────────
    case 'set-methods': {
      const methods: string[] = account.enabledPaymentMethods ?? [];
      if (!methods.length) return 'None yet';
      const label: Record<string, string> = {
        card: 'Card',
        cash: 'Cash',
        check: 'Check',
        venmo: 'Venmo',
        cashapp: 'Cash App',
      };
      return methods.map((m) => label[m] ?? m).join(', ');
    }
    case 'set-stripe':
      return account.stripeAccountId && account.stripeOnboardingComplete
        ? 'Connected'
        : 'Not connected';
    case 'set-overdue-audience':
      switch (account.overdueReminderAudience ?? 'CONTRACTOR') {
        case 'CUSTOMER':
          return 'Just the customer';
        case 'BOTH':
          return 'Both';
        case 'OFF':
          return 'No one';
        default:
          return 'Just me';
      }


    // ── Account ───────────────────────────────────────────────────────────
    case 'account-business-name':
      return text(account.businessName);
    case 'account-owner-name':
      return text(account.name);
    case 'account-email':
      return text(account.email);
    case 'account-timezone':
      return text(account.timezone);
    case 'account-service-area':
      return text(account.serviceArea);
    // Trade noun / Trade moved here from Phone assistant (#490). Trade
    // description is deliberately absent: it is a paragraph, and a search row
    // that renders one is a worse answer than the label alone.
    case 'account-trade-noun':
      return text(account.tradeNoun);
    case 'account-trade':
      return text(account.trade);

    default:
      return null;
  }
}
